import { HttpAgent } from '@icp-sdk/core/agent'
import ArbBridgeHelperClient, {
  ARB_BRIDGE_HELPER_CANISTER_ID,
  ARB_BRIDGE_HELPER_ETH_ADDRESS,
} from '../clients/ArbBridgeHelperClient'
import ICPSwap from '../clients/ICPSwap'
import { ARB_CHAIN_ID } from '../constants/business'
import { UNKNOWN_ERROR, UNSUPPORTED_TOKEN } from '../constants/swapErrors'
import { ICP_ICP_ID, USDC_ARB_ID, USDC_HYPERCORE_PERP_ID, USDC_ICP_ID } from '../constants/tokenIds'
import { approveToken } from '../handlers/walletActions/wallet'
import { PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
  SwapStep,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { addMultiplier, minBigInt } from '../utils/bigint'
import { asICToken } from '../utils/castToken'
import { GasCalculator } from '../utils/GasCalculator'
import { getEthProvider } from '../utils/getEthProvider'
import { getTokenBalance } from '../utils/getTokenBalances'
import { sendToken } from '../utils/sendToken'

// HyperLiquid perp-account deposit address on Arbitrum: bridge deposits land in
// the user's perp (clearinghouse) account.
const ARB_HYPERLIQUID_BRIDGE_DEPOSIT = '0x2Df1c51E09aECF9cacB7bc98cB1742757f163dF7'
// Fee estimation needs an address with a positive balance.
const WALLET_WITH_BALANCE = '0xb07d95FB29FCc6499e2F7Aa7205F1CA81c243cCa'

const FUNDING_SOURCE_IDS = [ICP_ICP_ID, USDC_ICP_ID, USDC_ARB_ID]

interface ArbGas {
  maxFeePerGas: bigint
  usdcArbTransferFee: bigint
}

// Funds the HyperCore perp account (`usdc-hypercore-perp`) from ICP-side sources
// by depositing USDC through the HyperLiquid Arbitrum bridge. Surfaced as a swap
// "provider" (reusing SwapProvider.HYPERLIQUID) so funding lives in the Swap UI.
// Routes: ICP -> USDC(ICP) -> USDC(Arb) -> bridge deposit; later sources skip the
// earlier hops.
class HyperliquidFundClient {
  agent: HttpAgent

  constructor(agent: HttpAgent) {
    this.agent = agent
  }

  static isSupportedPair(fromToken: PlugToken, toToken: PlugToken) {
    return toToken.id === USDC_HYPERCORE_PERP_ID && FUNDING_SOURCE_IDS.includes(fromToken.id)
  }

  async getQuote(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken,
    tokens: PlugToken[]
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    if (toToken.id !== USDC_HYPERCORE_PERP_ID) {
      throw new Error(UNSUPPORTED_TOKEN)
    }

    // ICP is swapped to USDC first, so its output depends on the ICPSwap rate.
    // USDC sources reach the perp account 1:1.
    if (fromToken.id === ICP_ICP_ID) {
      const usdcIcpToken = tokens.find((t) => t.id === USDC_ICP_ID)
      if (!usdcIcpToken) return { error: UNKNOWN_ERROR, provider: SwapProvider.HYPERLIQUID }

      const quote = await new ICPSwap(this.agent).getQuote(
        amount,
        asICToken(fromToken),
        asICToken(usdcIcpToken)
      )

      return { ...quote, provider: SwapProvider.HYPERLIQUID }
    }

    return {
      provider: SwapProvider.HYPERLIQUID,
      price: Number(amount),
      priceHuman: Number(amount) * 10 ** -fromToken.decimals,
    }
  }

  async swap(
    amount: bigint,
    fromToken: PlugToken,
    wallet: Wallet,
    tokens: PlugToken[],
    slippage: number,
    setStep: (step: SwapStep) => void
  ) {
    if (!wallet.ethAddress) throw new Error('Wallet ETH address not available')

    const usdcArbToken = tokens.find((t) => t.id === USDC_ARB_ID)
    const usdcIcpToken = tokens.find((t) => t.id === USDC_ICP_ID)
    if (!usdcArbToken || !usdcIcpToken) throw new Error('USDC token config not found')

    const gas = await this.#fetchArbGas(usdcArbToken)

    let amountToDeposit = amount

    // ICP -> USDC (ICP) via ICPSwap
    if (fromToken.id === ICP_ICP_ID) {
      setStep(SwapStep.Swap)
      const icpSwap = new ICPSwap(this.agent)
      const quote = await icpSwap.getQuote(amount, asICToken(fromToken), asICToken(usdcIcpToken))
      if ('error' in quote) throw new Error(quote.error)

      const priceLimit = Math.ceil(quote.price * (1 - slippage / 100))
      const swappedAmount = await icpSwap.swap(
        asICToken(fromToken),
        asICToken(usdcIcpToken),
        amount,
        priceLimit,
        wallet.principalId,
        setStep
      )

      const actualBalance = await this.#waitForSwappedBalance(wallet, usdcIcpToken, swappedAmount)
      amountToDeposit = minBigInt(BigInt(swappedAmount), actualBalance)
    }

    // USDC (ICP) -> USDC (Arb)
    if (fromToken.id !== USDC_ARB_ID) {
      setStep(SwapStep.Deposit)
      await this.#bridgeIcpUsdcToArb(amountToDeposit, usdcIcpToken, usdcArbToken, wallet, gas)
    }

    // USDC (Arb) -> HyperLiquid perp account
    setStep(SwapStep.Transfer)
    const actualArbBalance = await getTokenBalance(wallet, usdcArbToken, [ARB_CHAIN_ID])
    const depositAmount = minBigInt(amountToDeposit, BigInt(actualArbBalance))
    await sendToken(usdcArbToken, ARB_HYPERLIQUID_BRIDGE_DEPOSIT, depositAmount, wallet)

    return Number(amountToDeposit)
  }

  async #fetchArbGas(usdcArbToken: PlugToken): Promise<ArbGas> {
    if (!usdcArbToken.evmChain?.rpcEndpoint) throw new Error('USDCArb token config not found')

    const gasCalculator = new GasCalculator(usdcArbToken.evmChain)
    const usdcArbTransferFee = await gasCalculator.estimateErc20TransferGas(
      WALLET_WITH_BALANCE,
      usdcArbToken.address!,
      1
    )

    const { maxFeePerGas } = await getEthProvider(
      usdcArbToken.evmChain.rpcEndpoint
    ).estimateFeesPerGas()

    if (!usdcArbTransferFee || !maxFeePerGas) throw new Error('No gas fee available')

    return { maxFeePerGas, usdcArbTransferFee }
  }

  async #bridgeIcpUsdcToArb(
    amount: bigint,
    usdcIcpToken: PlugToken,
    usdcArbToken: PlugToken,
    wallet: Wallet,
    gas: ArbGas
  ) {
    if (!wallet.ethAddress) throw new Error('Wallet ETH address not available')
    if (!usdcArbToken.evmChain?.rpcEndpoint) throw new Error('USDCArb token config not found')

    await approveToken({
      spender: { address: ARB_BRIDGE_HELPER_CANISTER_ID },
      amount: amount.toString(),
      tokenCanisterId: usdcIcpToken.address!,
      standard: usdcIcpToken.standard,
    })

    const provider = getEthProvider(usdcArbToken.evmChain.rpcEndpoint)
    const arbBridgeHelperClient = new ArbBridgeHelperClient(this.agent)

    await arbBridgeHelperClient.bridgeUSDC({
      maxFeePerGas: gas.maxFeePerGas,
      gasLimit: gas.usdcArbTransferFee,
      nonce: await provider.getTransactionCount({
        address: ARB_BRIDGE_HELPER_ETH_ADDRESS as `0x${string}`,
      }),
      amount: amount - usdcIcpToken.fee,
      receiver: wallet.ethAddress,
    })
  }

  async #waitForSwappedBalance(wallet: Wallet, usdcIcpToken: PlugToken, amount: number) {
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    const cutoffMs = 2 * 60 * 1000
    const start = Date.now()
    const expectedAmount = addMultiplier(BigInt(amount), 0.9)

    let actualBalance = 0n
    while (actualBalance < expectedAmount) {
      if (Date.now() - start > cutoffMs) throw new Error('USDC did not arrive')
      await sleep(50)
      actualBalance = BigInt(await getTokenBalance(wallet, usdcIcpToken))
    }

    return actualBalance
  }
}

export default HyperliquidFundClient
